import { useEffect, useState } from 'react';

import { services } from '@/services';
import { ApiHttpError } from '@/types';

/**
 * useReferralAdminGate — 「推薦分潤管理」入口的雙閘(R392;docs/128 §0)
 * -----------------------------------------------------------------------------
 * owner 拍板:入口(側欄選單 + 頁面)只在 **role=admin ∧ `referral` flag on** 時
 * 顯示;非 admin / flag off(beta、prod 預設關)/ BE 未部署 → **整個入口隱藏**,
 * 不是顯示一個點了會噴錯的選單。
 *
 * flag 沒有查詢端點 → 以 **A22 `GET /admin/referral/periods?limit=1`** 當 probe
 * (最便宜的唯讀端點;60/min)。判準(R299 藏入口族):
 *   - 403(`FEATURE_DISABLED`,或 role 在 BE 端不認)→ `disabled`(藏入口)。
 *   - **任何 404** → `disabled`(BE 這批端點未部署)。A22 是清單端點、**沒有業務 404**
 *     (BE 取證:handler 不 raise 404),所以不分 slug —— FastAPI 的 JSON 404 解成
 *     `HTTP_404`、邊緣層的 HTML 404 解成 `NON_JSON_RESPONSE`,兩者都是「端點不在」;
 *     只認前者的話,HTML 404 會被當暫時性錯誤而把入口亮出來(R392.2 合併前檢查補上)。
 *   - 其他錯(網路 / 5xx / 429)→ `enabled`:**暫時性錯誤不等於 flag off** ——
 *     藏了就是把一次網路抖動放大成「功能消失」;入口留著,頁內誠實顯錯 + 重試
 *     (R350.8:有問題要看得見,禁止隱藏)。
 *
 * probe 結果 module-level cache(per session 一次):admin 逛全站不重打;
 * 非 admin **連 probe 都不發**(不對 admin 端點製造必 403 的呼叫)。
 */
export type ReferralGateState = 'unknown' | 'enabled' | 'disabled';

let cache: ReferralGateState = 'unknown';
let inflight: Promise<ReferralGateState> | null = null;
const listeners = new Set<(s: ReferralGateState) => void>();

function probe(): Promise<ReferralGateState> {
  inflight ??= services.referralService
    .listPeriods(1)
    .then((): ReferralGateState => 'enabled')
    .catch((err: unknown): ReferralGateState => {
      if (err instanceof ApiHttpError && (err.status === 403 || err.status === 404)) {
        return 'disabled';
      }
      console.warn('[referral-gate] probe failed (transient), keep entry visible', err);
      return 'enabled';
    })
    .then((state) => {
      cache = state;
      inflight = null;
      for (const l of listeners) l(state);
      return state;
    });
  return inflight;
}

/** @param isAdmin 非 admin 恆回 `disabled` 且不發 probe。 */
export function useReferralAdminGate(isAdmin: boolean): ReferralGateState {
  const [state, setState] = useState<ReferralGateState>(isAdmin ? cache : 'disabled');

  useEffect(() => {
    if (!isAdmin) {
      setState('disabled');
      return;
    }
    if (cache !== 'unknown') {
      setState(cache);
      return;
    }
    let alive = true;
    const listener = (s: ReferralGateState) => {
      if (alive) setState(s);
    };
    listeners.add(listener);
    void probe();
    return () => {
      alive = false;
      listeners.delete(listener);
    };
  }, [isAdmin]);

  return isAdmin ? state : 'disabled';
}

/** 測試用:清 module cache(probe 結果是 session 級 latch,測試間要歸零)。 */
export function resetReferralAdminGateForTest(): void {
  cache = 'unknown';
  inflight = null;
  listeners.clear();
}
